import React, { useState, useRef, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../AppContext';
import DiscoverySettings from './DiscoverySettings'; // Import the new unified component
import TrendingTagsSidebar from './TrendingTagsSidebar';
import withVisionAura from './visions/withVisionAura';

const ProfilePicture = withVisionAura(({ src, alt, onClick, className }) => (
    <img src={src} alt={alt} onClick={onClick} className={className} />
));

const ProfileDropdown = () => {
    const { t } = useTranslation();
    const { user, userProfiles, handlePageChange } = useAppContext();
    const currentUserProfile = userProfiles.find(p => p.id === user?.uid);
    const [isOpen, setIsOpen] = useState(false);
    const [isDiscoveryModalOpen, setIsDiscoveryModalOpen] = useState(false); // State for the new modal
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    if (!currentUserProfile) {
        return null;
    }

    return (
        <>
            <div className="relative" ref={dropdownRef}>
                <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 p-2 bg-gray-800/70 backdrop-blur-sm rounded-full text-white font-bold text-sm">
                    <ProfilePicture src={currentUserProfile.photoURL || "https://placehold.co/24x24/AEC6CF/FFFFFF?text=U"} alt="User" className="w-6 h-6 rounded-full object-cover" userId={currentUserProfile.id} />
                    <span className="text-shadow-md">{currentUserProfile.displayName}</span>
                    <LucideIcons.ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className="absolute top-full right-0 mt-2 w-80 bg-gray-800/90 backdrop-blur-sm rounded-lg shadow-lg p-4 z-40">
                        <div className="flex items-center gap-4 mb-4">
                            <img src={currentUserProfile.photoURL || "https://placehold.co/64x64/AEC6CF/FFFFFF?text=U"} alt="User" className="w-16 h-16 rounded-full object-cover border-2 border-purple-400"/>
                            <div>
                                <h3 className="font-bold text-lg text-white">{currentUserProfile.displayName}</h3>
                                <p className="text-sm text-gray-400">@{currentUserProfile.username || 'user'}</p>
                            </div>
                        </div>
                        <div className="flex justify-around text-center my-4">
                            <div>
                                <p className="font-bold text-xl text-white">{currentUserProfile.followersCount || 0}</p>
                                <p className="text-xs text-gray-400">{t('profileDropdown.followers')}</p>
                            </div>
                            <div>
                                <p className="font-bold text-xl text-white">{currentUserProfile.followingCount || 0}</p>
                                <p className="text-xs text-gray-400">{t('profileDropdown.following')}</p>
                            </div>
                            <div>
                                <p className="font-bold text-xl text-yellow-400 flex items-center justify-center gap-1">
                                    <LucideIcons.Gem size={16} />
                                    {currentUserProfile.tokens || 0}
                                </p>
                                <p className="text-xs text-gray-400">{t('profileDropdown.echoes')}</p>
                            </div>
                        </div>

                        {/* Constellation UI */}
                        <div className="mb-4 p-3 bg-indigo-900/30 rounded-lg border border-indigo-500/30">
                            <div className="flex justify-between items-center mb-1">
                                <span className="text-xs font-bold text-indigo-300">{t('profileDropdown.constellationLevel', { level: currentUserProfile.constellationLevel || 1 })}</span>
                                <span className="text-xs text-gray-400">
                                    {currentUserProfile.constellationXP || 0} / {Math.floor(100 * Math.pow(currentUserProfile.constellationLevel || 1, 1.8))} {t('profileDropdown.xp')}
                                </span>
                            </div>
                            <div className="w-full bg-gray-800 rounded-full h-1.5 mb-2 overflow-hidden">
                                <div 
                                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500" 
                                    style={{ width: `${Math.min(100, ((currentUserProfile.constellationXP || 0) / Math.floor(100 * Math.pow(currentUserProfile.constellationLevel || 1, 1.8))) * 100)}%` }}
                                ></div>
                            </div>
                            <button
                                onClick={() => { handlePageChange('connectedFeed'); setIsOpen(false); }}
                                className="w-full py-1.5 text-xs font-semibold text-indigo-200 hover:text-white hover:bg-indigo-500/20 rounded transition-colors flex items-center justify-center gap-1"
                            >
                                <LucideIcons.Sparkles size={12} />
                                {t('profileDropdown.viewConstellation')}
                            </button>
                        </div>

                        <button
                            onClick={() => { handlePageChange('viewingProfile', { userId: user.uid }); setIsOpen(false); }}
                            className="w-full py-2 bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                        >
                            {t('profileDropdown.viewMyProfile')}
                        </button>
                
                        <div className="my-4 border-t border-gray-700"></div>
                
                        {/* --- UNIFIED DISCOVERY BUTTON --- */}
                        <button
                            onClick={() => { setIsDiscoveryModalOpen(true); setIsOpen(false); }}
                            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-700 transition-colors text-left"
                        >
                            <div className="flex items-center gap-3">
                                <LucideIcons.Globe size={20} className="text-indigo-300" />
                                <span className="font-semibold text-white">{t('profileDropdown.discovery')}</span>
                            </div>
                            <LucideIcons.ChevronRight size={16} className="text-gray-400" />
                        </button>

                        <div className="my-4 border-t border-gray-700"></div>

                        <TrendingTagsSidebar />
                    </div>
                )}
            </div>

            {/* --- DISCOVERY MODAL --- */}
            {isDiscoveryModalOpen && <DiscoverySettings onClose={() => setIsDiscoveryModalOpen(false)} />}
        </>
    );
};

export default ProfileDropdown;
